import * as api from '@gitbook/api-client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/icons';
import { type ButtonProps, ButtonTab, ButtonTabs, useScreenType } from '@gitbook/spine-react';

import { useNavigation } from '../../../navigation';

/**
 * Button to switch between reading and preview mode.
 */
export function ViewModeButton(props: {
    screen: NavigationScreenSpaceState;
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
}) {
    const { space, screen, changeRequest } = props;

    const { navigate } = useNavigation();
    const { isMobile } = useScreenType();

    const isEditableMode = isEditable({ screen, space, changeRequest, isMobile });

    return (
        <ButtonTabs>
            {MODES.map((mode) => {
                if (
                    !mode.enabled({
                        screen,
                        space,
                        changeRequest,
                        isMobile,
                        isEditable: isEditableMode,
                    })
                ) {
                    return null;
                }

                const isActive = mode.active(screen);

                return (
                    <ButtonTab
                        key={mode.label}
                        label={mode.label}
                        title={mode.title({
                            active: isActive,
                            isEditable: isEditableMode,
                        })}
                        leadingIcon={mode.leadingIcon?.({ isEditable: isEditableMode })}
                        trailingIcon={mode.trailingIcon?.({ isEditable: isEditableMode })}
                        active={isActive}
                        onPress={() => {
                            navigate({
                                screen: (prev) => {
                                    if (prev.type !== 'space') {
                                        return prev;
                                    }

                                    return mode.href(prev);
                                },
                            });
                        }}
                    />
                );
            })}
        </ButtonTabs>
    );
}

/**
 * Check if the space is editable.
 */
function isEditable({
    space,
    screen,
    changeRequest,
    isMobile,
}: {
    screen: NavigationScreenSpaceState;
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    isMobile: boolean;
}) {
    return (
        !isMobile &&
        !screen.revision &&
        (space.editMode === api.SpaceEditMode.Live ||
            changeRequest?.status === api.ChangeRequestStatus.Draft ||
            changeRequest?.status === api.ChangeRequestStatus.Open)
    );
}

type Mode = {
    label: string;
    title: (input: { active: boolean; isEditable: boolean }) => string;
    enabled: (props: {
        screen: NavigationScreenSpaceState;
        space: api.Space;
        changeRequest: api.ChangeRequest | null;
        isMobile: boolean;
        isEditable: boolean;
    }) => boolean;
    active: (screen: NavigationScreenSpaceState) => boolean;
    href: (screen: NavigationScreenSpaceState) => NavigationScreenSpaceState;
    leadingIcon?: (props: { isEditable: boolean }) => ButtonProps['leadingIcon'];
    trailingIcon?: (props: { isEditable: boolean }) => ButtonProps['trailingIcon'];
};

/**
 * Available modes for the space.
 *
 */
const MODES: Mode[] = [
    {
        label: 'Editor',
        title: ({ active, isEditable }) => {
            return active
                ? isEditable
                    ? 'You are editing the pages and content'
                    : 'You are viewing the read-only content'
                : 'Edit the pages and content in the editor';
        },
        enabled: () => true,
        active: (screen) => !screen.mode,
        href: (screen) => ({
            ...screen,
            mode: undefined,
        }),
        trailingIcon: ({ isEditable }) => {
            return !isEditable ? <Icon icon="pencil-slash" /> : undefined;
        },
    },
    {
        label: 'Changes',
        title: ({ active }) =>
            active ? 'You are reviewing the changes' : 'Review the changes in this change request',
        enabled: ({ screen, changeRequest }) => !!changeRequest || !!screen.revision,
        active: (screen) => screen.mode === 'diff',
        href: (screen) => ({
            ...screen,
            mode: 'diff',
        }),
    },
    {
        label: 'Preview',
        title: ({ active }) =>
            active
                ? 'You are previewing the published site'
                : 'Preview the content in the published site',
        enabled: () => true,
        active: (screen) => screen.mode === 'preview',
        href: (screen) => ({
            ...screen,
            mode: 'preview',
        }),
    },
];
